import type { CustomCompType, GenericFieldConfig } from '@neo/blocks/forms/types'
import type { SelectOption } from '@neo/primitives/select'
import { FlowSelect } from 'v2/shared/components/flow-select'
import { FormSectionHeader } from 'v2/shared/components/form-section-header'
import { PhoneNumberSelect } from 'v2/shared/components/phonenumber-select'
import z from 'zod'

interface CallChannelSettingsForm {
  outbound_phlo: string
  default_caller_id: string
  channel_settings: {
    allow_noise_reduction: boolean
    allow_personal_caller_ids: boolean
    allow_recording_pause_play: boolean
    call_recording_inbound: boolean
    call_recording_outbound: boolean
  }
}

// Paginated remote select over /phlo/options (SER-5833) — replaces the
// fetch-all flows array that ratcheted memory on flow-heavy accounts.
const ActiveFlowSelect = (props: React.ComponentProps<typeof FlowSelect>) => (
  <FlowSelect {...props} activeOnly />
)

export const getCallChannelFields = () => {
  const callChannelFields: GenericFieldConfig<CallChannelSettingsForm> = [
    {
      name: 'outbound_phlo',
      label: 'Default Outgoing Call Agent',
      className: 'w-72',
      type: 'custom',
      component: ActiveFlowSelect as CustomCompType<CallChannelSettingsForm>,
      eventDataExtractor: (e) => e as string,
      inputProps: {
        'data-testid': 'select-outbound-call-agent',
      },
      zodValidation: z
        .string({ message: 'Select a default outgoing flow' })
        .min(1, 'Select a default outgoing flow'),
    },
    {
      name: 'default_caller_id',
      label: 'Default Caller ID',
      className: 'w-72',
      type: 'custom',
      zodValidation: z
        .string({ message: 'Select a default caller ID' })
        .min(1, 'Select a default caller ID'),
      component: PhoneNumberSelect as CustomCompType<CallChannelSettingsForm>,
      eventDataExtractor: (e) => e as string,
    },
    {
      name: 'channel_settings.allow_personal_caller_ids',
      label: 'Let agent select or change a caller ID when placing calls',
      componentType: 'switch',
      zodValidation: z.boolean({ message: 'This is a required field' }),
    },
    {
      name: 'record_incoming_calls_header_hr',
      type: 'slot',
      component: <hr className="mt-2" />,
    },
    {
      name: 'record_incoming_calls_header',
      type: 'slot',
      component: (
        <FormSectionHeader
          className="mt-2"
          title="Call Recording Preferences"
          subtitle="Manage your call recording preferences here"
        />
      ),
    },
    {
      name: 'channel_settings.call_recording_inbound',
      label: 'Record all incoming calls',
      componentType: 'switch',
      zodValidation: z.boolean(),
    },
    {
      name: 'channel_settings.call_recording_outbound',
      label: 'Record all outgoing calls',
      componentType: 'switch',
      zodValidation: z.boolean(),
    },
    {
      name: 'channel_settings.allow_recording_pause_play',
      label: 'Let agents pause and resume call recording',
      componentType: 'switch',
      zodValidation: z.boolean(),
    },
    {
      name: 'channel_settings.allow_noise_reduction',
      label: 'Enable noise reduction for all calls',
      componentType: 'switch',
      zodValidation: z.boolean(),
    },
  ]
  return callChannelFields
}

export const callChannelDefaultValues: CallChannelSettingsForm = {
  outbound_phlo: '',
  default_caller_id: '',
  channel_settings: {
    allow_noise_reduction: false,
    allow_personal_caller_ids: false,
    allow_recording_pause_play: false,
    call_recording_inbound: false,
    call_recording_outbound: false,
  },
}

// SMS Channel
interface SMSChannelSettingsForm {
  outbound_phlo: string
  default_caller_id: string
}

export const getSMSChannelFields = (ids: SelectOption[]) => {
  const callChannelFields: GenericFieldConfig<SMSChannelSettingsForm> = [
    {
      name: 'outbound_phlo',
      label: 'Default Outgoing SMS Agent',
      className: 'w-72',
      type: 'custom',
      component: ActiveFlowSelect as CustomCompType<SMSChannelSettingsForm>,
      eventDataExtractor: (e) => e as string,
      inputProps: {
        'data-testid': 'select-outbound-sms-agent',
      },
      zodValidation: z
        .string({ message: 'Select a default outgoing flow' })
        .min(1, 'Select a default outgoing flow'),
    },
    {
      name: 'default_caller_id',
      label: 'Default Sender ID',
      className: 'w-72',
      componentType: 'select',
      inputProps: {
        options: ids,
      },
      zodValidation: z
        .string({ message: 'Select a default sender ID' })
        .min(1, 'Select a default sender ID'),
    },
  ]
  return callChannelFields
}

export const SMSChannelDefaultValues: SMSChannelSettingsForm = {
  outbound_phlo: '',
  default_caller_id: '',
}

// WhatsApp Channel
type WAChannelSettingsForm = SMSChannelSettingsForm

export const getWAChannelFields = (ids: SelectOption[]) => {
  const WAChannelFields: GenericFieldConfig<WAChannelSettingsForm> = [
    {
      name: 'outbound_phlo',
      label: 'Default Outgoing WhatsApp Agent',
      className: 'w-72',
      type: 'custom',
      component: ActiveFlowSelect as CustomCompType<WAChannelSettingsForm>,
      eventDataExtractor: (e) => e as string,
      inputProps: {
        'data-testid': 'select-outbound-whatsapp-agent',
      },
      zodValidation: z
        .string({ message: 'Select a default outgoing flow' })
        .min(1, 'Select a default outgoing flow'),
    },
    {
      name: 'default_caller_id',
      label: 'Default Sender ID',
      className: 'w-72',
      componentType: 'select',
      inputProps: {
        options: ids,
      },
      zodValidation: z
        .string({ message: 'Select a default sender ID' })
        .min(1, 'Select a default sender ID'),
    },
  ]
  return WAChannelFields
}

export const WAChannelDefaultValues = SMSChannelDefaultValues

export const channelConfigFieldsMap = {
  call: {
    fields: getCallChannelFields,
    defaultValues: callChannelDefaultValues,
  },
  sms: {
    fields: getSMSChannelFields,
    defaultValues: SMSChannelDefaultValues,
  },
  whatsapp: {
    fields: getWAChannelFields,
    defaultValues: WAChannelDefaultValues,
  },
}
